import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Share2, Download, Copy, Crown, Sparkles, ShieldCheck, Twitter, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";

const TIER_BG = {
    platinum: "linear-gradient(135deg, #0F172A 0%, #1E293B 45%, #312E81 100%)",
    gold:     "linear-gradient(135deg, #F59E0B 0%, #F97316 50%, #E11D48 100%)",
    silver:   "linear-gradient(135deg, #94A3B8 0%, #64748B 50%, #475569 100%)",
    bronze:   "linear-gradient(135deg, #A16207 0%, #EA580C 50%, #DC2626 100%)",
    starter:  "linear-gradient(135deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)",
};

const TIER_ACCENT = {
    platinum: "#A855F7",
    gold:     "#FBBF24",
    silver:   "#CBD5E1",
    bronze:   "#F59E0B",
    starter:  "#A78BFA",
};

export default function ShareScoreDialog({ open, onClose }) {
    const { user } = useAuth();
    const [score, setScore] = useState(null);
    const [boosters, setBoosters] = useState(null);
    const [copied, setCopied] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        api.get("/credit/score/raw").then((r) => setScore(r.data));
        api.get("/credit/boosters").then((r) => setBoosters(r.data));
    }, [open]);

    if (!user) return null;

    const tier = boosters?.tier || { key: "starter", label: "Starter" };
    const grade = score?.grade || "—";
    const scoreNum = score?.score || user?.credit_score || 600;
    const firstName = user.full_name?.split(" ")[0] || "Friend";
    const bg = TIER_BG[tier.key] || TIER_BG.starter;
    const accent = TIER_ACCENT[tier.key] || TIER_ACCENT.starter;

    const shareText = `🎯 My OnePassCredit DNA Score is ${scoreNum} (${grade}) — ${tier.label} tier. Get yours in 60 seconds at OnePassCredit.`;
    const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/` : "";

    const doShare = async () => {
        const payload = { title: "My OnePassCredit DNA Score", text: shareText, url: shareUrl };
        if (typeof navigator !== "undefined" && navigator.share) {
            try {
                await navigator.share(payload);
                toast.success("Shared!");
            } catch {
                // user cancelled
            }
        } else {
            await copyText();
        }
    };

    const copyText = async () => {
        try {
            await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
            setCopied(true);
            toast.success("Copied to clipboard");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Couldn't copy");
        }
    };

    const downloadCard = async () => {
        try {
            const html2canvas = (await import("html2canvas")).default;
            if (!cardRef.current) return;
            const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2 });
            const link = document.createElement("a");
            link.download = `credit-dna-${scoreNum}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
            toast.success("Card downloaded");
        } catch (e) {
            console.warn("download error", e);
            toast.error("Couldn't generate image");
        }
    };

    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    const whatsAppUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-sm rounded-3xl p-0 overflow-hidden border-0" data-testid="share-score-dialog">
                <VisuallyHidden>
                    <DialogTitle>Share my Credit DNA Score</DialogTitle>
                    <DialogDescription>Generate a shareable card or copy a link.</DialogDescription>
                </VisuallyHidden>

                {/* The card itself (captured by html2canvas) */}
                <div className="p-5 bg-white">
                    <div
                        ref={cardRef}
                        className="rounded-2xl overflow-hidden shadow-2xl relative"
                        style={{ background: bg }}
                        data-testid="share-score-card"
                    >
                        <div className="p-6 text-white relative">
                            <div className="absolute top-3 right-3 opacity-50">
                                <Sparkles size={14} />
                            </div>
                            {/* Header */}
                            <div className="flex items-center gap-1.5">
                                <div className="size-5 rounded-md bg-white/20 flex items-center justify-center">
                                    <ShieldCheck size={11} />
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80">OnePassCredit</div>
                            </div>

                            {/* Main score */}
                            <div className="mt-6 text-center">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-bold">Credit DNA Score™</div>
                                <div className="font-display font-bold mt-1 leading-none" style={{ fontSize: "84px", color: accent, textShadow: "0 4px 24px rgba(0,0,0,0.3)" }}>
                                    {scoreNum}
                                </div>
                                <div className="text-base font-semibold mt-2 text-white">{grade}</div>
                            </div>

                            {/* Tier badge */}
                            <div className="mt-5 flex items-center justify-center gap-1.5 bg-white/10 backdrop-blur rounded-full px-4 py-1.5 mx-auto w-fit">
                                <Crown size={12} style={{ color: accent }} fill={accent} />
                                <span className="text-xs font-bold uppercase tracking-wider">{tier.label} tier</span>
                            </div>

                            {/* Footer */}
                            <div className="mt-6 pt-4 border-t border-white/15">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-[9px] uppercase tracking-wider text-white/60 font-bold">Holder</div>
                                        <div className="text-sm font-semibold mt-0.5">{firstName}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[9px] uppercase tracking-wider text-white/60 font-bold">Issued</div>
                                        <div className="text-sm font-semibold mt-0.5">{new Date().toLocaleDateString(undefined, { month: "short", year: "numeric" })}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-5 space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                            <a href={whatsAppUrl} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp">
                                <Button variant="outline" className="w-full h-11 rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                                    <MessageCircle size={16} />
                                </Button>
                            </a>
                            <a href={tweetUrl} target="_blank" rel="noopener noreferrer" data-testid="share-twitter">
                                <Button variant="outline" className="w-full h-11 rounded-xl border-sky-200 text-sky-700 hover:bg-sky-50">
                                    <Twitter size={16} />
                                </Button>
                            </a>
                            <Button
                                variant="outline"
                                className="w-full h-11 rounded-xl"
                                onClick={copyText}
                                data-testid="share-copy"
                            >
                                {copied ? <Check size={16} /> : <Copy size={16} />}
                            </Button>
                        </div>
                        <Button
                            onClick={doShare}
                            className="w-full h-11 rounded-xl bg-[#0A1930] hover:bg-[#0A1930]/90 font-semibold"
                            data-testid="share-system"
                        >
                            <Share2 size={16} /> Share
                        </Button>
                        <Button
                            onClick={downloadCard}
                            variant="outline"
                            className="w-full h-11 rounded-xl font-semibold"
                            data-testid="share-download"
                        >
                            <Download size={16} /> Download card
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
